import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import { updateAttributionTerms } from '../../Services/partners';

// Édition des TERMES d'une affaire déjà attribuée : le taux appliqué, le début et la fin
// de commissionnement.
//
// Ces deux valeurs sont figées à la création de l'affaire (taux depuis la grille, fenêtre
// dérivée du type). Cet écran est la porte de sortie assumée : prolongation négociée,
// arrêt anticipé, taux renégocié. Sans elle, la seule façon de corriger une affaire était
// de la clôturer et d'en recréer une — ce qui change sa date de début et brouille
// l'historique des commissions.
//
// Le TYPE d'affaire n'est volontairement PAS éditable ici : le changer recalcule la
// fenêtre de commissionnement, donc ce n'est pas la même intention que « corriger les
// termes ». Ça se fait à l'association, ou via l'API.

type Props = {
	attribution: {
		id: number;
		rate?: number;
		startAt?: string;
		commissionEndAt?: string;
		commissionType?: string;
	} | null;
	clientName?: string;
	onClose: () => void;
	onSaved: () => void;
};

// #region toDateInput — ISO → valeur d'un <input type="date">
const toDateInput = (iso?: string) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
// #endregion

// #region EditAttributionTermsDialog
export const EditAttributionTermsDialog: React.FC<Props> = ({ attribution, clientName, onClose, onSaved }) => {
	const notify = useNotify();
	const [rate, setRate] = useState('');
	const [startAt, setStartAt] = useState('');
	const [endAt, setEndAt] = useState('');
	const [saving, setSaving] = useState(false);

	// Réinitialisé à CHAQUE ouverture, sur l'affaire courante. Sans ça, ouvrir une
	// deuxième ligne afficherait les valeurs de la première — et on éditerait le taux d'un
	// client en croyant modifier celui d'un autre.
	useEffect(() => {
		if (!attribution) return;
		setRate(attribution.rate === undefined || attribution.rate === null ? '' : String(attribution.rate));
		setStartAt(toDateInput(attribution.startAt));
		setEndAt(toDateInput(attribution.commissionEndAt));
	}, [attribution]);

	if (!attribution) return null;

	const parsedRate = Number(rate);
	const rateInvalid = rate === '' || Number.isNaN(parsedRate) || parsedRate < 0 || parsedRate > 100;
	// Comparaison de chaînes AAAA-MM-JJ : l'ordre lexical est l'ordre chronologique.
	const datesInvalid = startAt === '' || (endAt !== '' && endAt < startAt);

	const save = () => {
		if (rateInvalid || datesInvalid) return;
		setSaving(true);
		// `commissionEndAt: ''` n'est PAS « ne rien envoyer » : l'API le lit comme « pas de
		// fin » et met la colonne à NULL. C'est le seul moyen de rendre une affaire
		// commissionnée sans échéance, et c'est exactement ce que veut dire un champ date
		// vidé par l'admin.
		updateAttributionTerms(attribution.id, { rate: parsedRate, startAt, commissionEndAt: endAt })
			.then(() => {
				notify('Termes mis à jour', { type: 'success' });
				onSaved();
				onClose();
			})
			.catch((e: any) => notify(`Erreur : ${e?.message || 'mise à jour impossible'}`, { type: 'error' }))
			.finally(() => setSaving(false));
	};

	return (
		<Dialog open onClose={onClose} fullWidth maxWidth="xs">
			<DialogTitle>Termes de l'affaire</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					{clientName || `Attribution #${attribution.id}`}
				</Typography>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
					<TextField
						autoFocus
						size="small"
						type="number"
						label="Taux de commission"
						value={rate}
						onChange={(e) => setRate(e.target.value)}
						error={rateInvalid && rate !== ''}
						InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
					/>
					<TextField
						size="small"
						type="date"
						label="Début de commissionnement"
						value={startAt}
						onChange={(e) => setStartAt(e.target.value)}
						InputLabelProps={{ shrink: true }}
						error={startAt === ''}
						helperText="Aucun encaissement antérieur à cette date ne commissionne."
					/>
					<TextField
						size="small"
						type="date"
						label="Fin de commissionnement"
						value={endAt}
						onChange={(e) => setEndAt(e.target.value)}
						InputLabelProps={{ shrink: true }}
						error={endAt !== '' && startAt !== '' && endAt < startAt}
						helperText={
							endAt !== '' && startAt !== '' && endAt < startAt
								? 'La fin précède le début.'
								: "Vide = pas de fin, le client commissionne tant qu'il paie."
						}
					/>
				</Box>

				{/* Dit ce que la modification NE fait PAS. Un admin qui corrige un taux
				    pourrait croire régulariser le passé ; les lignes déjà enregistrées portent
				    leur propre taux, figé au règlement. */}
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
					S'applique aux règlements à venir. Les commissions déjà enregistrées gardent le taux appliqué à leur date, et
					antidater le début ne rejoue pas les encaissements passés.
				</Typography>

				<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
					<Button onClick={onClose}>Annuler</Button>
					<Button variant="contained" onClick={save} disabled={saving || rateInvalid || datesInvalid}>
						Enregistrer
					</Button>
				</Box>
			</DialogContent>
		</Dialog>
	);
};
// #endregion
